import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Download,
  Upload,
  Search,
  Filter,
  MoreVertical,
  Clock,
  DollarSign,
  Calculator,
  Activity,
  TrendingUp,
  BarChart3,
  Users,
  Settings,
  Target,
  AlertTriangle,
  CheckCircle,
  Play,
  Pause,
  Square,
  RefreshCw,
  FileText,
  Calendar,
  Zap,
  Shield,
  Layers,
  GitBranch
} from 'lucide-react';

const EnhancedTimeMaterialsContract = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contractToDelete, setContractToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0
  });

  // Enhanced mock data for Time & Materials contracts
  const mockContracts = [
    {
      id: 'TM-001',
      name: 'Software Development Services',
      client: 'TechStart Inc',
      status: 'active',
      hourlyRate: 125,
      currency: 'USD',
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      totalHours: 1200,
      hoursLogged: 480,
      totalCost: 60000,
      costCeiling: 100000,
      progress: 40,
      timeTracking: {
        enabled: true,
        required: true,
        approvalRequired: true,
        maxHoursPerDay: 8,
        overtimeAllowed: false
      },
      costTracking: {
        enabled: true,
        categories: ['Development', 'Testing', 'Consulting'],
        budgetLimits: [50000, 30000, 20000],
        costCeiling: 100000
      },
      billingRules: {
        minimumHours: 0.25,
        roundingRules: 'quarter-hour',
        travelTime: false,
        administrativeTime: true
      },
      hourlyRates: [
        { role: 'Senior Developer', rate: 150, hours: 200, total: 30000 },
        { role: 'Junior Developer', rate: 100, hours: 150, total: 15000 },
        { role: 'Project Manager', rate: 125, hours: 80, total: 10000 },
        { role: 'QA Engineer', rate: 110, hours: 50, total: 5500 }
      ],
      materialCosts: [
        { category: 'Software Licenses', markup: 15, cost: 5000, total: 5750 },
        { category: 'Hardware', markup: 10, cost: 2000, total: 2200 },
        { category: 'Third-party Services', markup: 20, cost: 3000, total: 3600 }
      ],
      timeEntries: [
        {
          id: 1,
          date: '2024-01-15',
          employee: 'John Doe',
          role: 'Senior Developer',
          hours: 8,
          rate: 150,
          total: 1200,
          description: 'Frontend development',
          project: 'User Interface',
          status: 'approved',
          approvedBy: 'Project Manager',
          approvedDate: '2024-01-16'
        },
        {
          id: 2,
          date: '2024-01-15',
          employee: 'Jane Smith',
          role: 'Junior Developer',
          hours: 6,
          rate: 100,
          total: 600,
          description: 'Backend API development',
          project: 'API Integration',
          status: 'pending',
          approvedBy: null,
          approvedDate: null
        },
        {
          id: 3,
          date: '2024-01-16',
          employee: 'Mike Johnson',
          role: 'QA Engineer',
          hours: 4,
          rate: 110,
          total: 440,
          description: 'Testing and quality assurance',
          project: 'Quality Control',
          status: 'approved',
          approvedBy: 'Project Manager',
          approvedDate: '2024-01-17'
        }
      ],
      utilizationRate: 85,
      efficiencyScore: 92,
      riskLevel: 'low',
      teamMembers: [
        { id: 1, name: 'John Doe', role: 'Senior Developer', allocation: 100, hourlyRate: 150, hoursLogged: 200, totalCost: 30000 },
        { id: 2, name: 'Jane Smith', role: 'Junior Developer', allocation: 100, hourlyRate: 100, hoursLogged: 150, totalCost: 15000 },
        { id: 3, name: 'Mike Johnson', role: 'QA Engineer', allocation: 75, hourlyRate: 110, hoursLogged: 50, totalCost: 5500 },
        { id: 4, name: 'Sarah Wilson', role: 'Project Manager', allocation: 50, hourlyRate: 125, hoursLogged: 80, totalCost: 10000 }
      ],
      performanceMetrics: {
        budgetUtilization: 60,
        scheduleAdherence: 95,
        qualityScore: 88,
        clientSatisfaction: 92,
        teamProductivity: 85,
        costEfficiency: 90
      },
      risks: [
        {
          id: 1,
          title: 'Scope creep potential',
          level: 'medium',
          impact: 'cost',
          probability: 40,
          mitigation: 'Regular scope reviews and change control',
          owner: 'Project Manager',
          status: 'monitoring'
        }
      ]
    },
    {
      id: 'TM-002',
      name: 'IT Consulting Services',
      client: 'Enterprise Corp',
      status: 'active',
      hourlyRate: 200,
      currency: 'USD',
      startDate: '2024-02-01',
      endDate: '2024-08-31',
      totalHours: 800,
      hoursLogged: 320,
      totalCost: 64000,
      costCeiling: 150000,
      progress: 40,
      timeTracking: {
        enabled: true,
        required: true,
        approvalRequired: true,
        maxHoursPerDay: 10,
        overtimeAllowed: true
      },
      costTracking: {
        enabled: true,
        categories: ['Strategy', 'Implementation', 'Training'],
        budgetLimits: [60000, 50000, 40000],
        costCeiling: 150000
      },
      billingRules: {
        minimumHours: 0.5,
        roundingRules: 'half-hour',
        travelTime: true,
        administrativeTime: true
      },
      hourlyRates: [
        { role: 'Senior Consultant', rate: 200, hours: 200, total: 40000 },
        { role: 'Principal Consultant', rate: 250, hours: 80, total: 20000 },
        { role: 'Business Analyst', rate: 150, hours: 40, total: 6000 }
      ],
      materialCosts: [
        { category: 'Software Tools', markup: 20, cost: 3000, total: 3600 },
        { category: 'Training Materials', markup: 15, cost: 1000, total: 1150 },
        { category: 'Travel Expenses', markup: 0, cost: 5000, total: 5000 }
      ],
      timeEntries: [
        {
          id: 1,
          date: '2024-02-01',
          employee: 'Dr. Smith',
          role: 'Principal Consultant',
          hours: 8,
          rate: 250,
          total: 2000,
          description: 'Strategic planning session',
          project: 'Digital Transformation',
          status: 'approved',
          approvedBy: 'Director',
          approvedDate: '2024-02-02'
        }
      ],
      utilizationRate: 90,
      efficiencyScore: 95,
      riskLevel: 'low',
      teamMembers: [
        { id: 1, name: 'Dr. Smith', role: 'Principal Consultant', allocation: 100, hourlyRate: 250, hoursLogged: 80, totalCost: 20000 },
        { id: 2, name: 'Lisa Brown', role: 'Senior Consultant', allocation: 100, hourlyRate: 200, hoursLogged: 200, totalCost: 40000 },
        { id: 3, name: 'Tom Wilson', role: 'Business Analyst', allocation: 50, hourlyRate: 150, hoursLogged: 40, totalCost: 6000 }
      ],
      performanceMetrics: {
        budgetUtilization: 43,
        scheduleAdherence: 100,
        qualityScore: 95,
        clientSatisfaction: 98,
        teamProductivity: 90,
        costEfficiency: 95
      },
      risks: []
    }
  ];

  // Load contracts
  useEffect(() => {
    loadContracts();
  }, []);

  const loadContracts = async () => {
    setLoading(true);
    try {
      // Mock API call
      setContracts(mockContracts);
      setPagination(prev => ({ ...prev, total: mockContracts.length }));
    } catch (error) {
      console.error('Error loading contracts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteContract = async (contractId) => {
    setIsDeleting(true);
    try {
      // Mock API call
      setContracts(prev => prev.filter(contract => contract.id !== contractId));
      setShowDeleteModal(false);
      setContractToDelete(null);
    } catch (error) {
      console.error('Error deleting contract:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      case 'completed': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'approved': return 'text-green-600 bg-green-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getRiskLevelColor = (level) => {
    switch (level) {
      case 'low': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'high': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Contracts</p>
              <p className="text-2xl font-bold text-gray-900">{contracts.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <Clock className="h-6 w-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Hours Logged</p>
              <p className="text-2xl font-bold text-gray-900">
                {contracts.reduce((sum, c) => sum + c.hoursLogged, 0)}
              </p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <Activity className="h-6 w-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Cost</p>
              <p className="text-2xl font-bold text-gray-900">
                ${contracts.reduce((sum, c) => sum + c.totalCost, 0).toLocaleString()}
              </p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <DollarSign className="h-6 w-6 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Avg Utilization</p>
              <p className="text-2xl font-bold text-gray-900">
                {Math.round(contracts.reduce((sum, c) => sum + c.utilizationRate, 0) / contracts.length)}%
              </p>
            </div>
            <div className="p-3 bg-orange-100 rounded-full">
              <TrendingUp className="h-6 w-6 text-orange-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Contracts List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Time & Materials Contracts</h3>
            <div className="flex items-center gap-3">
              <button className="btn-modern-outline">
                <Download size={16} />
                Export
              </button>
              <button className="btn-modern-primary">
                <Plus size={16} />
                New Contract
              </button>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contract
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Client
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Hours Logged
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Utilization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Risk
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contracts.map((contract) => (
                <tr key={contract.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{contract.name}</div>
                      <div className="text-sm text-gray-500">ID: {contract.id}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{contract.client}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {contract.hoursLogged} / {contract.totalHours}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      ${contract.totalCost.toLocaleString()} {contract.currency}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-full bg-gray-200 rounded-full h-2 mr-3">
                        <div 
                          className="bg-blue-600 h-2 rounded-full" 
                          style={{ width: `${contract.utilizationRate}%` }}
                        ></div>
                      </div>
                      <span className="text-sm text-gray-600">{contract.utilizationRate}%</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contract.status)}`}>
                      {contract.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getRiskLevelColor(contract.riskLevel)}`}>
                      {contract.riskLevel}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <button className="text-blue-600 hover:text-blue-900">
                        <Eye size={16} />
                      </button>
                      <button className="text-green-600 hover:text-green-900">
                        <Edit size={16} />
                      </button>
                      <button 
                        className="text-red-600 hover:text-red-900"
                        onClick={() => {
                          setContractToDelete(contract);
                          setShowDeleteModal(true);
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderTimeTracking = () => (
    <div className="space-y-6">
      {contracts.map((contract) => (
        <div key={contract.id} className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{contract.name}</h3>
            <p className="text-sm text-gray-600">Client: {contract.client}</p>
          </div>
          
          <div className="p-6">
            <div className="mb-6">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Time Entries</h4>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Hours</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Rate</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {contract.timeEntries.map((entry) => (
                      <tr key={entry.id}>
                        <td className="px-4 py-2 text-sm text-gray-900">{entry.date}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{entry.employee}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{entry.role}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">{entry.hours}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">${entry.rate}</td>
                        <td className="px-4 py-2 text-sm font-medium text-gray-900">${entry.total}</td>
                        <td className="px-4 py-2 text-sm">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(entry.status)}`}>
                            {entry.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-4">Hourly Rates Summary</h4>
                <div className="space-y-3">
                  {contract.hourlyRates.map((rate, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">{rate.role}</div>
                        <div className="text-sm text-gray-600">{rate.hours} hours</div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-gray-900">${rate.rate}/hr</div>
                        <div className="text-sm text-gray-600">${rate.total.toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-4">Material Costs Summary</h4>
                <div className="space-y-3">
                  {contract.materialCosts.map((cost, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">{cost.category}</div>
                        <div className="text-sm text-gray-600">{cost.markup}% markup</div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-gray-900">${cost.total.toLocaleString()}</div>
                        <div className="text-sm text-gray-600">Base: ${cost.cost.toLocaleString()}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderCostTracking = () => (
    <div className="space-y-6">
      {contracts.map((contract) => (
        <div key={contract.id} className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{contract.name}</h3>
            <p className="text-sm text-gray-600">Client: {contract.client}</p>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  ${contract.totalCost.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">Total Cost</div>
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  ${contract.costCeiling.toLocaleString()}
                </div>
                <div className="text-sm text-gray-600">Cost Ceiling</div>
              </div>
              
              <div className="text-center">
                <div className={`text-3xl font-bold mb-2 ${
                  (contract.totalCost / contract.costCeiling) > 0.8 ? 'text-red-600' : 'text-green-600'
                }`}>
                  {Math.round((contract.totalCost / contract.costCeiling) * 100)}%
                </div>
                <div className="text-sm text-gray-600">Budget Utilization</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-4">Cost Categories</h4>
                <div className="space-y-3">
                  {contract.costTracking.categories.map((category, index) => (
                    <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div className="font-medium text-gray-900">{category}</div>
                      <div className="text-sm text-gray-600">
                        Budget: ${contract.costTracking.budgetLimits[index]?.toLocaleString()}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-md font-semibold text-gray-900 mb-4">Team Cost Breakdown</h4>
                <div className="space-y-3">
                  {contract.teamMembers.map((member) => (
                    <div key={member.id} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                      <div>
                        <div className="font-medium text-gray-900">{member.name}</div>
                        <div className="text-sm text-gray-600">{member.role}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-gray-900">${member.totalCost.toLocaleString()}</div>
                        <div className="text-sm text-gray-600">{member.hoursLogged}h @ ${member.hourlyRate}/hr</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderPerformanceMetrics = () => (
    <div className="space-y-6">
      {contracts.map((contract) => (
        <div key={contract.id} className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">{contract.name}</h3>
            <p className="text-sm text-gray-600">Client: {contract.client}</p>
          </div>
          
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 mb-2">
                  {contract.performanceMetrics.budgetUtilization}%
                </div>
                <div className="text-sm text-gray-600">Budget Utilization</div>
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 mb-2">
                  {contract.performanceMetrics.scheduleAdherence}%
                </div>
                <div className="text-sm text-gray-600">Schedule Adherence</div>
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-bold text-purple-600 mb-2">
                  {contract.performanceMetrics.qualityScore}%
                </div>
                <div className="text-sm text-gray-600">Quality Score</div>
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-bold text-orange-600 mb-2">
                  {contract.performanceMetrics.clientSatisfaction}%
                </div>
                <div className="text-sm text-gray-600">Client Satisfaction</div>
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-bold text-indigo-600 mb-2">
                  {contract.performanceMetrics.teamProductivity}%
                </div>
                <div className="text-sm text-gray-600">Team Productivity</div>
              </div>
              
              <div className="text-center">
                <div className="text-3xl font-bold text-teal-600 mb-2">
                  {contract.performanceMetrics.costEfficiency}%
                </div>
                <div className="text-sm text-gray-600">Cost Efficiency</div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return renderOverview();
      case 'time-tracking':
        return renderTimeTracking();
      case 'cost-tracking':
        return renderCostTracking();
      case 'performance':
        return renderPerformanceMetrics();
      default:
        return renderOverview();
    }
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate('/contracts')}
            className="btn-modern-secondary"
          >
            <ArrowLeft size={16} />
            Back to Contracts
          </button>
        </div>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Time & Materials Contracts</h1>
            <p className="page-subtitle">
              Manage T&M contracts with comprehensive time tracking, cost management, and performance monitoring
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            <button className="btn-modern-outline">
              <Download size={16} />
              Export Report
            </button>
            <button className="btn-modern-primary">
              <Plus size={16} />
              New Contract
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="page-tabs">
        <div className="page-tabs-list">
          <button
            className={`page-tabs-item ${activeTab === 'overview' ? 'page-tabs-item-active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            <BarChart3 size={16} />
            Overview
          </button>
          <button
            className={`page-tabs-item ${activeTab === 'time-tracking' ? 'page-tabs-item-active' : ''}`}
            onClick={() => setActiveTab('time-tracking')}
          >
            <Clock size={16} />
            Time Tracking
          </button>
          <button
            className={`page-tabs-item ${activeTab === 'cost-tracking' ? 'page-tabs-item-active' : ''}`}
            onClick={() => setActiveTab('cost-tracking')}
          >
            <DollarSign size={16} />
            Cost Tracking
          </button>
          <button
            className={`page-tabs-item ${activeTab === 'performance' ? 'page-tabs-item-active' : ''}`}
            onClick={() => setActiveTab('performance')}
          >
            <TrendingUp size={16} />
            Performance
          </button>
        </div>
      </div>

      {/* Tab Content */}
      <div className="page-tab-content">
        {renderTabContent()}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mt-4">Delete Contract</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to delete "{contractToDelete?.name}"? This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-center gap-3 mt-4">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="btn-modern-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleDeleteContract(contractToDelete?.id)}
                  disabled={isDeleting}
                  className="btn-modern-danger"
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedTimeMaterialsContract;
